
import React, { useState, useEffect } from 'react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  LineChart, Line, Legend, PieChart, Pie, Cell
} from 'recharts';
import { 
  Database, Sparkles, Loader2, Check, Lightbulb, BarChart2, ArrowRight, Trash2, Bot, CheckCircle2, Globe, Layers, ChevronLeft, ChevronRight, FileText, Zap, AlertTriangle
} from 'lucide-react';
import Markdown from 'react-markdown';
import { Survey, DataCategory } from '../types';
import { fetchSurveysPaginated, analyzeSurveys, AIAnalysisResponse } from '../services/surveyService';
import { fetchDataCategories } from '../services/categoryService';

const ResearchCopilot: React.FC = () => {
    // Tab State
    const [activeTab, setActiveTab] = useState<'CAMPAIGNS' | 'PUBLIC'>('CAMPAIGNS');

    // Data State
    const [surveys, setSurveys] = useState<Survey[]>([]);
    const [categories, setCategories] = useState<DataCategory[]>([]);
    
    // Pagination State
    const [campaignPage, setCampaignPage] = useState(1);
    const [totalCampaigns, setTotalCampaigns] = useState(0);
    const CAMPAIGN_LIMIT = 8; // Smaller limit for shorter box

    // Selection State
    const [selectedSurveyIds, setSelectedSurveyIds] = useState<Set<string>>(new Set());
    const [selectedCategoryIds, setSelectedCategoryIds] = useState<Set<number>>(new Set());
    
    // Dashboard State
    const [prompt, setPrompt] = useState('');
    const [result, setResult] = useState<any | null>(null);
    const [loading, setLoading] = useState(false);
    const [loadingSurveys, setLoadingSurveys] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        loadCategories();
    }, []);

    useEffect(() => {
        loadCampaigns();
    }, [campaignPage]);

    const loadCampaigns = async () => {
        setLoadingSurveys(true);
        try {
            const { data, count } = await fetchSurveysPaginated(campaignPage, CAMPAIGN_LIMIT);
            setSurveys(data);
            setTotalCampaigns(count);
        } catch (e) {
            console.error("Failed to load campaigns", e);
        } finally {
            setLoadingSurveys(false);
        }
    };

    const loadCategories = async () => {
        try {
            const categoryData = await fetchDataCategories();
            setCategories(categoryData);
        } catch (e) {
            console.error(e);
        }
    };

    const toggleSurvey = (id: string) => {
        const newSet = new Set(selectedSurveyIds);
        if (newSet.has(id)) newSet.delete(id);
        else newSet.add(id);
        setSelectedSurveyIds(newSet);
    };

    const toggleCategory = (id: number) => {
        const newSet = new Set(selectedCategoryIds);
        if (newSet.has(id)) newSet.delete(id);
        else newSet.add(id);
        setSelectedCategoryIds(newSet);
    };

    const handleAnalyze = async () => {
        if (!prompt.trim()) return;
        // Allow analysis even without selection if the query implies semantic search over all data
        // if (selectedSurveyIds.size === 0 && selectedCategoryIds.size === 0) ... 

        setLoading(true);
        setResult(null);
        setError(null);

        try {
            const response = await analyzeSurveys(
                prompt,
                Array.from(selectedSurveyIds),
                Array.from(selectedCategoryIds),
                []
            );
            setResult(response);
        } catch (e: any) {
            console.error(e);
            // Surface the real reason (out of credits, embedding/model error, …) —
            // a generic message hides what actually went wrong.
            setError(e?.message || "Analysis failed. Please check your connection and try again.");
        } finally {
            setLoading(false);
        }
    };

    // Allow Ctrl/Cmd+Enter to run the analysis from the textarea.
    const handlePromptKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            e.preventDefault();
            handleAnalyze();
        }
    };

    // --- Custom Chart Component ---
    const DynamicChart = ({ config }: { config: any }) => {
        if (!config || !config.data || config.data.length === 0) return null;
        
        const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ec4899', '#8b5cf6', '#06b6d4'];

        return (
            <div className="w-full h-full">
                <ResponsiveContainer width="100%" height="100%">
                    {config.type === 'bar' ? (
                        <BarChart data={config.data}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                            <XAxis dataKey={config.xAxisKey} stroke="#94a3b8" fontSize={10} tickLine={false} axisLine={false} />
                            <YAxis stroke="#94a3b8" fontSize={10} tickLine={false} axisLine={false} />
                            <Tooltip 
                                contentStyle={{ backgroundColor: '#1e293b', borderColor: '#475569', color: '#fff' }} 
                                itemStyle={{ color: '#fff' }}
                                cursor={{fill: '#334155', opacity: 0.2}}
                            />
                            <Legend />
                            {config.dataKeys.map((key: string, i: number) => (
                                <Bar key={key} dataKey={key} fill={COLORS[i % COLORS.length]} radius={[4, 4, 0, 0]} />
                            ))}
                        </BarChart>
                    ) : (
                        <div className="flex items-center justify-center h-full text-slate-500 text-sm">Visualization available</div>
                    )}
                </ResponsiveContainer>
            </div>
        );
    };

    const maxPages = Math.ceil(totalCampaigns / CAMPAIGN_LIMIT);
    
    // Check if we have the rich engine output
    const engineData = result?.raw_engine_output || {};

    return (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 animate-in fade-in duration-500 pb-12">
            {/* Sidebar: Source Selector */}
            <div className="lg:col-span-1 bg-slate-800 rounded-xl border border-slate-700 flex flex-col overflow-hidden shadow-lg lg:sticky lg:top-4 h-[600px]">
                
                {/* Header */}
                <div className="p-4 border-b border-slate-700 bg-slate-850">
                    <h3 className="text-white font-bold text-sm uppercase tracking-wider flex items-center gap-2">
                        <Database size={16} className="text-indigo-400" /> Data Sources
                    </h3>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-slate-700 bg-slate-900/50">
                    <button 
                        onClick={() => setActiveTab('CAMPAIGNS')}
                        className={`flex-1 py-3 text-xs font-bold uppercase tracking-wider transition-colors border-b-2 ${activeTab === 'CAMPAIGNS' ? 'text-white border-indigo-500 bg-slate-800' : 'text-slate-500 border-transparent hover:text-slate-300'}`}
                    >
                        My Campaigns
                    </button>
                    <button 
                        onClick={() => setActiveTab('PUBLIC')}
                        className={`flex-1 py-3 text-xs font-bold uppercase tracking-wider transition-colors border-b-2 ${activeTab === 'PUBLIC' ? 'text-white border-emerald-500 bg-slate-800' : 'text-slate-500 border-transparent hover:text-slate-300'}`}
                    >
                        Public Data
                    </button>
                </div>
                
                <div className="flex-1 overflow-y-auto custom-scrollbar relative">
                    {/* Survey Section */}
                    {activeTab === 'CAMPAIGNS' && (
                        <div className="p-3">
                            {loadingSurveys ? (
                                <div className="flex justify-center py-8"><Loader2 className="animate-spin text-indigo-500" size={24}/></div>
                            ) : (
                                <div className="space-y-1">
                                    {surveys.length === 0 && <p className="text-slate-500 text-xs italic px-2 py-4 text-center">No campaigns found.</p>}
                                    {surveys.map(s => (
                                        <div 
                                            key={s.id} 
                                            onClick={() => toggleSurvey(s.id)}
                                            className={`p-2 rounded-lg border cursor-pointer transition-all flex items-start gap-2 ${
                                                selectedSurveyIds.has(s.id) 
                                                ? 'bg-indigo-900/30 border-indigo-500/50' 
                                                : 'bg-slate-900/30 border-slate-700/50 hover:bg-slate-800'
                                            }`}
                                        >
                                            <div className={`mt-0.5 w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 transition-all ${
                                                selectedSurveyIds.has(s.id) ? 'bg-indigo-500 border-indigo-500' : 'border-slate-600'
                                            }`}>
                                                {selectedSurveyIds.has(s.id) && <Check size={10} className="text-white" />}
                                            </div>
                                            <div className="overflow-hidden">
                                                <p className={`text-xs font-medium truncate ${selectedSurveyIds.has(s.id) ? 'text-white' : 'text-slate-400'}`}>
                                                    {s.title.en}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Public Data Section */}
                    {activeTab === 'PUBLIC' && (
                        <div className="p-3">
                            <div className="space-y-1">
                                {categories.length === 0 && <p className="text-slate-500 text-xs italic px-2 py-4 text-center">No categories available.</p>}
                                {categories.map(c => (
                                    <div 
                                        key={c.id} 
                                        onClick={() => toggleCategory(c.id)}
                                        className={`p-2 rounded-lg border cursor-pointer transition-all flex items-start gap-2 ${
                                            selectedCategoryIds.has(c.id) 
                                            ? 'bg-emerald-900/20 border-emerald-500/40' 
                                            : 'bg-slate-900/30 border-slate-700/50 hover:bg-slate-800'
                                        }`}
                                    >
                                        <div className={`mt-0.5 w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 transition-all ${
                                            selectedCategoryIds.has(c.id) ? 'bg-emerald-500 border-emerald-500' : 'border-slate-600'
                                        }`}>
                                            {selectedCategoryIds.has(c.id) && <Check size={10} className="text-white" />}
                                        </div>
                                        <div className="overflow-hidden">
                                            <div className="flex items-center gap-1.5">
                                                <Globe size={10} className="text-emerald-400"/>
                                                <p className={`text-xs font-medium truncate ${selectedCategoryIds.has(c.id) ? 'text-emerald-300' : 'text-slate-400'}`}>
                                                    {c.name}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-3 border-t border-slate-700 bg-slate-850">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <button onClick={() => setCampaignPage(p => Math.max(1, p - 1))} disabled={campaignPage === 1} className="p-1 rounded bg-slate-700 text-slate-300 disabled:opacity-30"><ChevronLeft size={14} /></button>
                            <span className="text-xs text-slate-400 font-mono">{campaignPage}/{maxPages || 1}</span>
                            <button onClick={() => setCampaignPage(p => Math.min(maxPages, p + 1))} disabled={campaignPage >= maxPages} className="p-1 rounded bg-slate-700 text-slate-300 disabled:opacity-30"><ChevronRight size={14} /></button>
                        </div>
                        <span className="text-[10px] text-slate-500">{selectedSurveyIds.size + selectedCategoryIds.size} sources</span>
                    </div>
                </div>
            </div>

            {/* Main Area: Insight Engine Dashboard */}
            <div className="lg:col-span-3 flex flex-col gap-8">
                
                {/* 1. Input Section */}
                <div className="bg-slate-800 rounded-xl border border-slate-700 p-6 shadow-lg relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-4 opacity-10 pointer-events-none"><Sparkles size={120} /></div>
                    <h2 className="text-white font-bold text-lg mb-4 flex items-center gap-2 relative z-10">
                        <Bot className="text-indigo-400" size={20} /> Tinaney Insight Engine
                    </h2>
                    <div className="flex flex-col gap-4 relative z-10">
                        <textarea
                            value={prompt}
                            onChange={e => setPrompt(e.target.value)}
                            onKeyDown={handlePromptKeyDown}
                            placeholder="Ask a complex question (e.g. 'Analyze the economic sentiment in Battambang compared to official rice yield data')..."
                            className="w-full bg-slate-900 border border-slate-600 rounded-xl p-4 text-slate-200 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all resize-none h-24 text-sm"
                        />
                        <div className="flex justify-between items-center">
                            <span className="text-xs text-slate-500 flex items-center gap-1">
                                <Sparkles size={12} className="text-purple-400"/> Powered by Vector Semantic Search
                                <span className="hidden sm:inline text-slate-600 ml-2">· ⌘/Ctrl + Enter to run</span>
                            </span>
                            <button
                                onClick={handleAnalyze}
                                disabled={loading || !prompt.trim()}
                                className="bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-700 disabled:text-slate-500 text-white px-6 py-2.5 rounded-lg font-bold shadow-lg active:scale-95 transition-all flex items-center gap-2 text-sm"
                            >
                                {loading ? <Loader2 className="animate-spin" size={16} /> : <Zap size={16} />}
                                {loading ? 'Processing Vectors...' : 'Run Analysis'}
                            </button>
                        </div>
                    </div>
                </div>

                {/* 2. Output Section */}
                <div className="flex flex-col">
                    <div className="w-full">
                        {error && !loading && (
                            <div className="mb-6 flex items-center gap-3 bg-red-900/20 border border-red-500/40 text-red-200 px-4 py-3 rounded-xl text-sm animate-in fade-in">
                                <AlertTriangle size={16} className="shrink-0 text-red-400" />
                                <span>{error}</span>
                            </div>
                        )}
                        {loading ? (
                            <div className="py-20 flex flex-col items-center justify-center text-slate-500 gap-4 border border-dashed border-slate-800 rounded-xl bg-slate-800/10">
                                <Loader2 className="animate-spin text-indigo-500" size={48} />
                                <p className="animate-pulse font-mono text-sm">Retrieving semantic fragments...</p>
                            </div>
                        ) : result ? (
                            <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                                
                                {/* A. Executive Summary */}
                                <div className="bg-gradient-to-r from-slate-800 to-indigo-900/20 p-6 rounded-xl border border-indigo-500/30">
                                    <div className="flex items-center justify-between mb-3 gap-3">
                                        <h4 className="text-indigo-300 font-bold text-sm uppercase tracking-wider flex items-center gap-2">
                                            <FileText size={16}/> Semantic Summary
                                        </h4>
                                        {typeof result.confidence === 'number' && (
                                            <span className={`text-[11px] font-mono px-2 py-1 rounded-full border shrink-0 ${
                                                result.confidence >= 0.66
                                                    ? 'text-emerald-300 bg-emerald-900/20 border-emerald-500/30'
                                                    : result.confidence >= 0.33
                                                    ? 'text-yellow-300 bg-yellow-900/20 border-yellow-500/30'
                                                    : 'text-red-300 bg-red-900/20 border-red-500/30'
                                            }`}>
                                                {Math.round(result.confidence * 100)}% confidence
                                            </span>
                                        )}
                                    </div>
                                    {result.sufficient_data === false && (
                                        <div className="mb-3 flex items-center gap-2 text-amber-200 bg-amber-900/20 border border-amber-500/30 rounded-lg px-3 py-2 text-xs">
                                            <AlertTriangle size={14} className="shrink-0 text-amber-400" />
                                            Limited data: this answer is not fully supported by the available sources.
                                        </div>
                                    )}
                                    <div className="text-slate-200 leading-relaxed text-lg font-medium">
                                        <Markdown
                                            components={{
                                                p: ({ ...props }) => <p className="mb-4 last:mb-0" {...props} />,
                                                ul: ({ ...props }) => <ul className="list-disc pl-6 mb-4" {...props} />,
                                                ol: ({ ...props }) => <ol className="list-decimal pl-6 mb-4" {...props} />,
                                                li: ({ ...props }) => <li className="mb-1" {...props} />,
                                                h1: ({ ...props }) => <h1 className="text-2xl font-bold mb-4 text-white" {...props} />,
                                                h2: ({ ...props }) => <h2 className="text-xl font-bold mb-3 text-white" {...props} />,
                                                h3: ({ ...props }) => <h3 className="text-lg font-bold mb-2 text-white" {...props} />,
                                                strong: ({ ...props }) => <strong className="font-bold text-white" {...props} />,
                                                em: ({ ...props }) => <em className="italic text-slate-300" {...props} />,
                                                blockquote: ({ ...props }) => <blockquote className="border-l-4 border-indigo-500 pl-4 italic my-4" {...props} />,
                                                code: ({ ...props }) => <code className="bg-slate-900 px-1.5 py-0.5 rounded text-indigo-300 font-mono text-sm" {...props} />,
                                            }}
                                        >
                                            {result.summary}
                                        </Markdown>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* B. Strategic Recommendation */}
                                    {engineData.strategic_recommendation && (
                                        <div className="bg-emerald-900/10 border border-emerald-500/30 p-6 rounded-xl">
                                            <h4 className="text-emerald-400 font-bold text-sm uppercase tracking-wider mb-3 flex items-center gap-2">
                                                <CheckCircle2 size={16}/> Strategic Recommendation
                                            </h4>
                                            <div className="text-emerald-100 text-sm leading-relaxed prose prose-invert max-w-none">
                                                <Markdown
                                                    components={{
                                                        p: ({ ...props }) => <p className="mb-2 last:mb-0" {...props} />,
                                                        ul: ({ ...props }) => <ul className="list-disc pl-4 mb-2" {...props} />,
                                                        li: ({ ...props }) => <li className="mb-1" {...props} />,
                                                        strong: ({ ...props }) => <strong className="font-bold text-white" {...props} />,
                                                    }}
                                                >
                                                    {engineData.strategic_recommendation}
                                                </Markdown>
                                            </div>
                                        </div>
                                    )}

                                    {/* C. Anomalies */}
                                    {engineData.anomalies && engineData.anomalies.length > 0 && (
                                        <div className="bg-orange-900/10 border border-orange-500/30 p-6 rounded-xl">
                                            <h4 className="text-orange-400 font-bold text-sm uppercase tracking-wider mb-3 flex items-center gap-2">
                                                <AlertTriangle size={16}/> Data Anomalies
                                            </h4>
                                            <ul className="space-y-2">
                                                {engineData.anomalies.map((anom: string, i: number) => (
                                                    <li key={i} className="text-orange-100 text-sm flex gap-2 items-start">
                                                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-orange-500 shrink-0" />
                                                        {anom}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>

                                {/* D. Top Themes */}
                                {engineData.top_themes && (
                                    <div className="bg-slate-800 p-6 rounded-xl border border-slate-700">
                                        <h4 className="text-indigo-400 font-bold text-sm uppercase tracking-wider mb-6 flex items-center gap-2">
                                            <Lightbulb size={16}/> Detected Themes
                                        </h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                            <div className="space-y-4">
                                                {engineData.top_themes.map((t: any, i: number) => (
                                                    <div key={i} className="group">
                                                        <div className="flex justify-between items-end mb-1">
                                                            <span className="text-white font-bold">{t.theme}</span>
                                                            <span className="text-xs text-indigo-400 font-mono">{(parseFloat(t.strength) * 100).toFixed(0)}% Match</span>
                                                        </div>
                                                        <div className="h-2 bg-slate-700 rounded-full overflow-hidden mb-2">
                                                            <div className="h-full bg-indigo-500" style={{ width: `${parseFloat(t.strength) * 100}%` }} />
                                                        </div>
                                                        <p className="text-xs text-slate-400">{t.description}</p>
                                                    </div>
                                                ))}
                                            </div>
                                            <div className="h-[250px]">
                                                <DynamicChart config={result.visualization} />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* E. References / Sources used */}
                                {engineData.references && engineData.references.length > 0 && (
                                    <div className="bg-slate-800 p-6 rounded-xl border border-slate-700">
                                        <h4 className="text-slate-300 font-bold text-sm uppercase tracking-wider mb-4 flex items-center gap-2">
                                            <Database size={16} className="text-indigo-400"/> Sources & References
                                        </h4>
                                        <ul className="space-y-3">
                                            {engineData.references.map((ref: any, i: number) => (
                                                <li key={i} className="flex gap-3 items-start text-sm">
                                                    <span className="mt-0.5 text-xs font-mono text-indigo-400 shrink-0">[{i + 1}]</span>
                                                    <div>
                                                        <p className="text-slate-200 font-medium">{ref.source_name || 'Unknown Source'}</p>
                                                        {ref.context && <p className="text-slate-400 text-xs mt-0.5">{ref.context}</p>}
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="py-20 flex flex-col items-center justify-center text-slate-500 opacity-40 border border-dashed border-slate-800 rounded-xl bg-slate-800/10">
                                <div className="w-24 h-24 rounded-full bg-slate-800 flex items-center justify-center mb-6">
                                    <Bot size={48} />
                                </div>
                                <p className="text-lg font-medium">Ready to Analyze</p>
                                <p className="text-sm mt-2">Enter a query to search across the entire data ledger.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ResearchCopilot;
